import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";

import cls from "./ChatList.module.scss";

import avatarPng from "@/assets/UI/admin.webp";
import api from "@/utils/axios";
import { useAuth } from "@/app/auth-context-state";
import { connectSocket, getSocket, releaseSocket } from "@/utils/socket";

function formatRelativeTime(dateInput) {
  if (!dateInput) return "";
  const created =
    typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(created.getTime())) return "";

  const now = new Date();
  const diffMs = now - created;
  if (diffMs < 0) return "";

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "just now";

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return `${diffMin} min${diffMin > 1 ? "s" : ""} ago`;
  }

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) {
    return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;
  }

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) {
    return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;
  }

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    return `${diffMonth} month${diffMonth > 1 ? "s" : ""} ago`;
  }

  const diffYear = Math.floor(diffMonth / 12);
  return `${diffYear} year${diffYear > 1 ? "s" : ""} ago`;
}

const ChatList = () => {
  const location = useLocation();
  const isChat = location.pathname === "/lounge/chat";
  const { user } = useAuth();
  const currentUserId = user?.id || user?._id || null;
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query] = useState("");
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());

   const accessToken = (() => {
    try {
      return localStorage.getItem("cm_at");
    } catch {
      return null;
    }
  })();

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/v1/chat/conversations");
      setConversations(res.data?.conversations || []);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          "Unable to load chats right now. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!accessToken) return;
    const socket = getSocket() || connectSocket(accessToken);

    const handlePresenceList = (payload) => {
      const ids = Array.isArray(payload) ? payload : [];
      setOnlineUserIds(new Set(ids.map((id) => String(id))));
    };

    const handleOnline = (payload) => {
      const userId = payload?.userId;
      if (!userId) return;
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.add(String(userId));
        return next;
      });
    };

    const handleOffline = (payload) => {
      const userId = payload?.userId;
      if (!userId) return;
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        next.delete(String(userId));
        return next;
      });
    };

    socket.on("presence:list", handlePresenceList);
    socket.on("presence:online", handleOnline);
    socket.on("presence:offline", handleOffline);

    return () => {
      const s = getSocket();
      if (s) {
        s.off("presence:list", handlePresenceList);
        s.off("presence:online", handleOnline);
        s.off("presence:offline", handleOffline);
      }
      releaseSocket();
    };
  }, [accessToken]);

  const chats = useMemo(() => {
    const pickTimestamp = (conversation) => {
      const lastMessage = conversation.lastMessage;
      const candidate =
        lastMessage?.createdAt ||
        conversation.lastMessageAt ||
        conversation.updatedAt ||
        conversation.createdAt;
      const timestamp = candidate ? new Date(candidate).getTime() : 0;
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    const normalized = new Map();

    conversations.forEach((conversation) => {
      const participants = conversation.participants || [];
      const otherParticipants = currentUserId
        ? participants.filter(
            (participant) => participant.userId !== currentUserId,
          )
        : participants;
      const target = otherParticipants[0] || participants[0] || null;
      const targetKey = target?.userId || conversation.id || conversation._id;
      const timestamp = pickTimestamp(conversation);
      const existing = normalized.get(targetKey);
      if (!existing || timestamp > existing.timestamp) {
        normalized.set(targetKey, {
          conversation,
          target,
          timestamp,
        });
      }
    });

    return Array.from(normalized.values()).map(({ conversation, target }) => {
      const lastMessage = conversation.lastMessage;
      const lastMessageText = lastMessage?.text?.trim();
      const preview =
        lastMessageText ||
        (lastMessage?.attachments?.length
          ? "Sent an attachment"
          : "No messages yet");
      const unread =
        lastMessage &&
        String(lastMessage.senderId) !== String(currentUserId) &&
        lastMessage.status !== "read"
          ? 1
          : 0;
      const timeLabel = formatRelativeTime(
        lastMessage?.createdAt || conversation.lastMessageAt,
      );
      return {
        id: conversation.id || conversation._id,
        name: target?.username || "Conversation",
        avatar: target?.avatarUrl || avatarPng,
        participantUserId: target?.userId || null,
        isOnline: target?.userId
          ? onlineUserIds.has(String(target.userId))
          : false,
        lastMessage: preview,
        time: timeLabel,
        unread,
      };
    });
  }, [conversations, currentUserId, onlineUserIds]);

  const filteredChats = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return chats;
    return chats.filter((chat) => {
      const nameMatch = chat.name.toLowerCase().includes(trimmed);
      const msgMatch = chat.lastMessage?.toLowerCase().includes(trimmed);
      return nameMatch || msgMatch;
    });
  }, [chats, query]);

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        <nav className={cls.tabs}>
          <Link to="/lounge" className={cls.tabButton}>
            Public
          </Link>
          <Link to="/lounge/friends-feed" className={cls.tabButton}>
            Friends
          </Link>
          <Link to="/lounge/clubs-feed" className={cls.tabButton}>
            Clubs
          </Link>
          <Link
            to="/lounge/chat"
            className={`${cls.tabButton} ${isChat ? cls.activeTab : ""}`}
          >
            Chat
          </Link>
          <Link to="/lounge/search" className={cls.tabButton}>
            🔍 Search
          </Link>
        </nav>

        <div className={cls.chatLayout}>
          <section className={cls.chatListPanel}>
            <header className={cls.panelHeader}>
              <div>
                <h1 className={cls.panelTitle}>Chats</h1>
                <p className={cls.panelSubtitle}>
                  Keep up with your latest connections.
                </p>
              </div>
            </header>

            <div className={cls.chatList}>
              {loading && <p>Loading chats...</p>}
              {!loading && error && <p>{error}</p>}
              {!loading && !error && filteredChats.length === 0 && (
                <p>No chats yet. Start a conversation to see it here.</p>
              )}
              {!loading &&
                !error &&
                filteredChats.map((chat) => (
                  <Link
                    key={chat.id}
                    to={`/lounge/chat/${chat.id}`}
                    className={cls.chatRow}
                  >
                    <div className={cls.avatarWrap}>
                      <img src={chat.avatar} alt={chat.name} />
                      <span
                        className={`${cls.onlineDot} ${
                          chat.isOnline
                            ? cls.onlineDotOnline
                            : cls.onlineDotOffline
                        }`}
                      />
                    </div>
                    <div className={cls.chatMeta}>
                      <div className={cls.chatTopRow}>
                        <span className={cls.chatName}>{chat.name}</span>
                        <span className={cls.chatTime}>{chat.time}</span>
                      </div>
                      <div className={cls.chatBottomRow}>
                        <span
                          className={`${cls.presenceLabel} ${
                            chat.isOnline
                              ? cls.presenceOnline
                              : cls.presenceOffline
                          }`}
                        >
                          {chat.isOnline ? "Online" : "Offline"}
                        </span>
                        <span className={cls.chatPreview}>
                          {chat.lastMessage}
                        </span>
                        {chat.unread > 0 && (
                          <span className={cls.unreadBadge}>{chat.unread}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </section>

          <section className={cls.chatEmptyPanel}>
            <div className={cls.emptyCard}>
              <h2>Open a conversation</h2>
              <p>
                Select a chat to see messages. Your conversations stay synced
                across every lounge tab.
              </p>
              <div className={cls.emptyActions}>
                <Link to="/lounge/search" className={cls.primaryButton}>
                  Find new friends
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ChatList;
